import wsClient from "src/wsClient"
import { TRAINING_GAME_ID } from "src/tutorial/scenario"
import { MAX_REACTIONS_ON_SCREEN, MAX_REACTIONS_PER_PLAYER } from "src/utils/reactions"
import { noteServerTime } from "src/utils/dates"

const fromPayload = (game) => {
  // The one door every game payload comes through, socket and REST alike, so the clock sample is
  // taken here rather than in each thunk. Not state: it is the device's error, not the game's.
  if (game.server_time) noteServerTime(game.server_time)
  return fromPayloadShape(game)
}

const fromPayloadShape = (game) => ({
  id: game.id,
  status: game.status,
  winnerTeam: game.winner_team,
  /* Who outlasted everybody (SV14). A survival game's verdict is a *person*, not a side — every
     player is on the same team — so the winning side cannot name the winner and `winner_team`
     says only that the people took it. Null in a draw, and in every classic game, where the
     team is the whole answer. */
  winnerUser: game.winner_user ? { id: game.winner_user.id, username: game.winner_user.username } : null,
  mode: game.mode,
  // How hard every wave bot plays (SV3): one difficulty for the whole survival game, chosen in
  // the lobby and stored on the game row. Null in a classic game, where a bot's difficulty is
  // the host's per-seat choice and rides on the seat.
  survivalDifficulty: game.survival_difficulty ?? null,
  tick: game.tick,
  tickTime: game.tick_time,
  // How long this game's rounds wait, chosen in the lobby (2026-08-21).
  roundSeconds: game.round_seconds,
  host: {
    id: game.host.id,
    username: game.host.username,
    rankingPoints: game.host.ranking_points,
    league: game.host.league ?? null,
  },
  // Who stopped the clock, or null. While set nobody's round advances; anybody may resume.
  pausedBy: game.paused_by ? { id: game.paused_by.id, username: game.paused_by.username } : null,
  // When the pause lapses by itself, and how many pauses each player has spent (security audit,
  // 2026-10-25: a pause budget and a time limit, both the server's).
  pausedUntil: game.paused_until ?? null,
  pausesUsed: game.pauses_used ?? {},
  metadata: game.state,
  // Whether this game moves anybody's ranking — the server's rule, read rather than re-derived
  // (a client copy of it printed "moved no ranking" from a rule that could drift from the payout).
  ranked: game.ranked ?? null,
  // Which world the game is played on (requirement 35): "S" | "M" | "L". The game payload names it
  // and every snapshot records it; anything from before the sizes was played on L.
  mapSize: game.map_size ?? game.state?.map_size ?? "L",
  players: game.players.map((player) => ({
    user: {
      id: player.user.id,
      username: player.user.username,
      rankingPoints: player.user.ranking_points,
      // The league the rating reads as (owner, 2026-10-08), the server's: it frames the icon.
      league: player.user.league ?? null,
      // What the player wears, the picture included: without it every other seat drew the
      // default face (owner, 2026-10-09). Resolved by the server, so it is always drawable.
      equippedCosmetics: player.user.equipped_cosmetics ?? null,
      // A seat the server plays: a bot has no socket to lose, so a connection state was true
      // and misleading at once. The roster labels it by its difficulty instead.
      isBot: player.user.is_bot,
    },
    team: player.team,
    race: player.race,
    // "easy" / "hard" / "impossible", null for a person: the rung this seat plays at, beside
    // the class it plays (requirement 31, 2026-09-20). It is a per-game choice the host makes,
    // not an identity the account carries. The `user` fallback is the cutover only — a server
    // still on the old shape keeps its plates — and this reducer is the one place that knows it.
    botDifficulty: player.bot_difficulty ?? player.user?.bot_difficulty ?? null,
    // The colour this seat plays as, agreed in the lobby — see `seatIconFor`.
    unitIcon: player.unit_icon,
    ready: player.ready,
    // Presence: whether that player's game socket is open. Drives the "host disconnected"
    // notice and the offer to claim the clock.
    connected: player.connected,
    initialLocations: player.initial_locations,
    // Out of the game (owner's R2): the server's round gate skips them, and so must the
    // host's client — an eliminated bot never readies again.
    eliminated: Boolean(player.eliminated),
    /* The seat's own verdict (SV14): "win" | "loss" | "draw", written once when the game
       finishes and null until then. A survival game needs it because every person is on the
       winning team and only one of them outlasted the rest; a classic game carries it too and
       the screens there still read the side, which is unchanged. */
    outcome: player.outcome ?? null,
    // The tick of the round this seat's last crown fell in, or null while they still stand.
    // It is what the standings sort on, and what "rounds survived" prints.
    eliminatedAtTick: player.eliminated_at_tick ?? null,
    // What this game paid the seat, or null: live, unranked, or a bot.
    rankingDelta: player.ranking_delta ?? null,
  })),
  // Who is watching. A spectator's own client also recognises itself by *not* finding a
  // player row; this list is for showing the room it has eyes.
  spectators: (game.spectators || []).map((user) => ({
    id: user.id,
    username: user.username,
  })),
})

/* Whether a game belongs in the live list: unfinished, and real. Basic training's scripted
   board goes through `setGame` like any game so the view mounts on it, and it is nobody's live
   game. */
const belongsInList = (game) => game && game.status !== "finished" && String(game.id) !== String(TRAINING_GAME_ID)

const upsert = (state, game) => {
  const index = state.games.findIndex((known) => known.id === game.id)
  if (!belongsInList(game)) {
    if (index >= 0) state.games.splice(index, 1)
  } else if (index >= 0) {
    state.games[index] = game
  } else {
    state.games.push(game)
  }
}

/* Make `game` the one being viewed. */
const view = (state, incoming) => {
  /* Looking back at a past round is something done to one live game (H8): a different game, or
     this one finishing — review mode takes over and has its own stepper — puts it away. */
  if (state.history && (!incoming || incoming.id !== state.game?.id || incoming.status === "finished")) {
    state.history = null
  }
  // A different game (first load, or a new one): none of the last game's talk, which is not this
  // game's to read.
  if (incoming && incoming.id !== state.game?.id) {
    state.chatMessages = []
  }
  state.game = incoming
}

/* A game's latest payload, and the one to be viewing: every socket broadcast, the lobby's
   "the game has started", a replay's snapshot and basic training all land here. The list is
   kept in step on the way through. */
/* Keep the log a payload carries, under its game and round (`requirements/36` P3). */
const MOTIONS_KEPT = 4
const rememberMotion = (state, game) => {
  const motion = game?.metadata?.motion
  if (!motion?.steps?.length || game.id == null || game.tick == null) return
  const id = String(game.id)
  const kept = { ...(state.motions?.[id] || {}), [game.tick]: motion }
  const ticks = Object.keys(kept).map(Number).sort((a, b) => b - a).slice(0, MOTIONS_KEPT)
  state.motions = { ...(state.motions || {}), [id]: Object.fromEntries(ticks.map((tick) => [tick, kept[tick]])) }
}

const setGame = (state, action) => {
  const games = action.payload.map(fromPayload)
  const incoming = games.length >= 1 ? games[0] : null
  games.forEach((game) => rememberMotion(state, game))
  if (incoming) upsert(state, incoming)
  view(state, incoming)
  return state
}

/* The bootstrap list (`GET /game/`): every live game of this account, and nothing about which
   one is being viewed — that is the URL's, through `enterGame`. A game being viewed that the list
   still carries is refreshed from it (the socket open re-reads the list for the seats' presence);
   one it no longer carries — finished since, a replay, basic training — is left as it is. */
const setGames = (state, action) => {
  const games = action.payload.map(fromPayload).filter(belongsInList)
  games.forEach((game) => rememberMotion(state, game))
  state.games = games
  const viewed = state.game && games.find((game) => game.id === state.game.id)
  if (viewed) view(state, viewed)
  return state
}

/* The route named a game: view it, out of the list. Unknown ids leave the store alone, and the
   guard that dispatched this is what answers the player. */
const enterGame = (state, action) => {
  const game = state.games.find((known) => String(known.id) === String(action.payload))
  if (game && state.game?.id !== game.id) view(state, game)
  return state
}


/* Watch the game through one player's eyes (owner's ask, 2026-09-04). `null` goes back to the
   logged-in player, which is the only state an ordinary game is ever in. */
const setSpectatingAs = (state, action) => {
  state.spectatingAs = action.payload ?? null
  return state
}

/* "Spectator" (false) or "Watching as" the chosen seat, through its fog (true). See initialState. */
const setWatchingThroughFog = (state, action) => {
  state.watchingThroughFog = Boolean(action.payload)
  return state
}

/* Look back at a round of the live game (H2), or `null` to come back to it. `{tick, game, loading}`
   — see initialState. Never through `setGame`: the live game underneath keeps taking the socket's
   payloads while the past one is on screen. */
const setHistory = (state, action) => {
  state.history = action.payload ?? null
  return state
}

const clearHistory = (state) => {
  state.history = null
  return state
}

/* The steps arrived for a round the player asked to see. Applied only if that is still the round
   being waited for: a player who pressed "Back to round N" — or stepped on — while the fetch was in
   flight must not be thrown back into the past by its answer. */
const historyLoaded = (state, action) => {
  const loaded = action.payload
  if (!loaded || !state.history || state.history.tick !== loaded.tick) return state
  state.history = loaded
  return state
}

const setRoundTrail = (state, action) => {
  state.roundTrail = action.payload ?? null
  return state
}

/* By round: `RoundFlash` clears the trail of the round it announced, when its seconds are up and
   when the tick moves on. The next round's trail arrives on the same frame as its board, so a
   clear that did not name its round would wipe the new trail from the old caption's cleanup. No
   payload clears whatever is there. */
const clearRoundTrail = (state, action) => {
  const tick = action?.payload?.tick
  if (tick != null && state.roundTrail && state.roundTrail.tick !== tick) return state
  state.roundTrail = null
  return state
}

const setRoundMotion = (state, action) => {
  state.roundMotion = action.payload ?? null
  return state
}

// By round, for the reason `clearRoundTrail` is: the next round's log lands with its board.
const clearRoundMotion = (state, action) => {
  const tick = action?.payload?.tick
  if (tick != null && state.roundMotion && state.roundMotion.tick !== tick) return state
  state.roundMotion = null
  return state
}

const setPlayerPresence = (state, action) => {
  const { userId, connected } = action.payload
  const player = state.game?.players.find((p) => p.user.id === userId)
  if (player) player.connected = connected
  return state
}

const leaveGame = (state) => {
  // Stop viewing: the list of live games stays, since the game goes on without this tab and the
  // home page lists it. Requirement 06 needs this working: leaving a finished game is how a
  // player gets back to the lobby flow.
  state.game = null
  state.history = null
  state.roundTrail = null
  state.roundMotion = null
  state.spectatingAs = null
  state.watchingThroughFog = false
  state.chatMessages = []
  state.reactions = []
  wsClient.close("game")
  return state
}

const addChatMessage = (state, action) => {
  state.chatMessages.push(action.payload)
  return state
}

/* A reaction arrived. Two caps are applied here rather than in the component, because this is the
   last point where a flood can be stopped before it is state:

   - at most two on screen from any one player, so somebody mashing the button takes two slots and
     everybody else still gets through;
   - at most five in total, oldest dropped first.

   The server rate-limits the sender too, but that limit is per connection and a patched client can
   ignore it altogether. The screen being filled is the one that gets to decide what it draws. */
const addReaction = (state, action) => {
  const arriving = action.payload
  if (state.reactions.some((reaction) => reaction.eventId === arriving.eventId)) return state

  const fromSamePlayer = state.reactions.filter((reaction) => reaction.userId === arriving.userId)
  const dropped = fromSamePlayer.length >= MAX_REACTIONS_PER_PLAYER ? fromSamePlayer[0].eventId : null

  state.reactions = [...state.reactions.filter((reaction) => reaction.eventId !== dropped), arriving].slice(
    -MAX_REACTIONS_ON_SCREEN
  )
  return state
}

const removeReaction = (state, action) => {
  state.reactions = state.reactions.filter((reaction) => reaction.eventId !== action.payload.eventId)
  return state
}

const setUserReady = (state, action) => {
  state.game = {
    ...state.game,
    players: state.game.players.map((p) => {
      if (p.user.id === action.payload.userId) {
        return { ...p, ready: true }
      }
      return p
    }),
  }
  return state
}

const reducers = {
  setGame,
  setGames,
  enterGame,
  setSpectatingAs,
  setWatchingThroughFog,
  setHistory,
  clearHistory,
  historyLoaded,
  setRoundTrail,
  clearRoundTrail,
  setRoundMotion,
  clearRoundMotion,
  setPlayerPresence,
  leaveGame,
  addChatMessage,
  addReaction,
  removeReaction,
  setUserReady,
}

export default reducers
